import { morph } from '@theme/morph';
import { Component } from '@theme/component';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';
import { DialogComponent, DialogCloseEvent } from '@theme/dialog';
import { mediaQueryLarge, isMobileBreakpoint, getIOSVersion, isAbortError } from '@theme/utilities';
import VariantPicker from '@theme/variant-picker';

/** @type {Map<string, Element>} */
const sharedQuickAddContentCache = new Map();
/** @type {Map<string, Promise<Element | null>>} */
const quickAddInFlightRequests = new Map();
/** @type {number} Monotonic id so only the latest quick-add open updates the shared modal */
let quickAddModalRequestId = 0;
/** @type {string | null} Product page URL currently rendered in the shared quick-add modal */
let lastModalProductUrl = null;

/** @param {unknown} error @param {string} message */
function logUnlessAbortError(error, message) {
  if (!isAbortError(error)) {
    console.error(message, error);
  }
}

function resetQuickAddModalContent() {
  const modalContent = document.getElementById('quick-add-modal-content');
  modalContent?.replaceChildren();
  lastModalProductUrl = null;
}

export class QuickAddComponent extends Component {
  /** @type {AbortController | null} */
  #abortController = null;
  /** @type {Map<string, Element>} */
  #cachedContent = new Map();
  /** @type {AbortController} */
  #cartUpdateAbortController = new AbortController();
  /** @type {(event: ProductSelectEvent) => void} */
  #handleProductSelectUpdate = this.#updateQuickAddButtonState.bind(this);
  /** @type {number | null} */
  #prefetchTimeoutId = null;
  /** @type {number | null} */
  #prefetchIdleId = null;

  get productPageUrl() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    const hotspotProduct = /** @type {import('./product-hotspot').ProductHotspotComponent | null} */ (
      this.closest('product-hotspot-component')
    );
    const hotspotLink =
      hotspotProduct && typeof hotspotProduct.getHotspotProductLink === 'function'
        ? hotspotProduct.getHotspotProductLink()
        : null;
    const productLink = productCard?.getProductCardLink() || hotspotLink;

    if (!productLink?.href) return '';

    const url = new URL(productLink.href);

    if (url.searchParams.has('variant')) {
      return url.toString();
    }

    const selectedVariantId = this.#getSelectedVariantId();
    if (selectedVariantId) {
      url.searchParams.set('variant', selectedVariantId);
    }

    return url.toString();
  }

  /**
   * Gets the currently selected variant ID from the product card
   * @returns {string | null} The variant ID or null
   */
  #getSelectedVariantId() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    return productCard?.getSelectedVariantId() || null;
  }

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#closeQuickAddModal);
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartUpdate, {
      signal: this.#cartUpdateAbortController.signal,
    });
    document.addEventListener(StandardEvents.productSelect, this.#handleProductSelectUpdate);
    this.addEventListener('pointerenter', this.#prefetchOnIntent, { passive: true });
    this.addEventListener('focusin', this.#prefetchOnIntent);
    this.addEventListener('touchstart', this.#prefetchOnIntent, { passive: true });
    this.#schedulePrefetch();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#cancelScheduledPrefetch();
    mediaQueryLarge.removeEventListener('change', this.#closeQuickAddModal);
    document.removeEventListener(StandardEvents.productSelect, this.#handleProductSelectUpdate);
    this.removeEventListener('pointerenter', this.#prefetchOnIntent);
    this.removeEventListener('focusin', this.#prefetchOnIntent);
    this.removeEventListener('touchstart', this.#prefetchOnIntent);

    const pendingUrl = this.productPageUrl;
    const pendingRequest = pendingUrl ? quickAddInFlightRequests.get(pendingUrl) : undefined;
    if (pendingRequest) {
      void pendingRequest.catch((error) => logUnlessAbortError(error, 'Quick-add prefetch abandoned on disconnect'));
    }

    // Drop without aborting — abort() during disconnect races with shared in-flight prefetch promises.
    this.#abortController = null;

    this.#cartUpdateAbortController.abort();
    this.#cartUpdateAbortController = new AbortController();
  }

  /**
   * Clears the cached content when cart is updated
   */
  #handleCartUpdate = () => {
    this.#cachedContent.clear();
    sharedQuickAddContentCache.clear();
    for (const request of quickAddInFlightRequests.values()) {
      void request.catch((error) => logUnlessAbortError(error, 'Quick-add prefetch abandoned on cart update'));
    }
    quickAddInFlightRequests.clear();
  };

  #prefetchOnIntent = () => {
    if (this.dataset.quickAddButton !== 'choose') return;
    this.#cancelScheduledPrefetch();
    void this.#prefetchProductGrid(this.productPageUrl);
  };

  #schedulePrefetch() {
    if (this.dataset.quickAddButton !== 'choose') return;
    this.#cancelScheduledPrefetch();

    if ('requestIdleCallback' in window) {
      this.#prefetchIdleId = window.requestIdleCallback(() => {
        this.#prefetchIdleId = null;
        void this.#prefetchProductGrid(this.productPageUrl);
      });
      return;
    }

    this.#prefetchTimeoutId = setTimeout(() => {
      this.#prefetchTimeoutId = null;
      void this.#prefetchProductGrid(this.productPageUrl);
    }, 200);
  }

  #cancelScheduledPrefetch() {
    if (this.#prefetchTimeoutId !== null) {
      window.clearTimeout(this.#prefetchTimeoutId);
      this.#prefetchTimeoutId = null;
    }

    if (this.#prefetchIdleId !== null && 'cancelIdleCallback' in window) {
      window.cancelIdleCallback(this.#prefetchIdleId);
      this.#prefetchIdleId = null;
    }
  }

  /**
   * Prefetches and caches product grid HTML for quick-add (e.g. hover on product card).
   * @param {string} productPageUrl
   * @returns {Promise<Element | null>}
   */
  prefetchProductGrid(productPageUrl) {
    return this.#loadProductGrid(productPageUrl);
  }

  /**
   * Fire-and-forget prefetch that logs real failures but stays quiet on abort.
   * @param {string} productPageUrl
   */
  #prefetchProductGrid(productPageUrl) {
    void this.prefetchProductGrid(productPageUrl).catch((error) => {
      logUnlessAbortError(error, 'Quick-add prefetch failed');
    });
  }

  /**
   * Loads and caches product grid HTML for quick-add.
   * @param {string} productPageUrl
   * @returns {Promise<Element | null>}
   */
  async #loadProductGrid(productPageUrl) {
    if (!productPageUrl) return null;

    const localCachedGrid = this.#cachedContent.get(productPageUrl);
    if (localCachedGrid) return localCachedGrid;

    const sharedCachedGrid = sharedQuickAddContentCache.get(productPageUrl);
    if (sharedCachedGrid) {
      this.#cachedContent.set(productPageUrl, sharedCachedGrid);
      return sharedCachedGrid;
    }

    let inFlightRequest = quickAddInFlightRequests.get(productPageUrl);
    if (!inFlightRequest) {
      inFlightRequest = (async () => {
        const html = await this.fetchProductPage(productPageUrl);
        if (!html) return null;
        const gridElement = html.querySelector('[data-product-grid-content]');
        if (!gridElement) return null;
        return /** @type {Element} */ (gridElement.cloneNode(true));
      })();
      quickAddInFlightRequests.set(productPageUrl, inFlightRequest);
    }

    try {
      const productGrid = await inFlightRequest;
      if (productGrid) {
        sharedQuickAddContentCache.set(productPageUrl, productGrid);
        this.#cachedContent.set(productPageUrl, productGrid);
      }
      return productGrid;
    } finally {
      if (quickAddInFlightRequests.get(productPageUrl) === inFlightRequest) {
        quickAddInFlightRequests.delete(productPageUrl);
      }
    }
  }

  /**
   * Re-renders the variant picker in the quick-add modal.
   * @param {Element} newHtml - The element to re-render.
   */
  #updateVariantPicker(newHtml) {
    const modalContent = document.getElementById('quick-add-modal-content');
    if (!modalContent) return;
    const variantPicker = /** @type {VariantPicker | null} */ (modalContent.querySelector('variant-picker'));
    if (!variantPicker || !newHtml.querySelector('variant-picker')) return;
    variantPicker.updateVariantPicker(newHtml);
  }

  /**
   * Handles quick add button click
   * @param {Event} event - The click event
   */
  handleClick = async (event) => {
    event.preventDefault();

    const triggerButton = event.currentTarget instanceof HTMLButtonElement ? event.currentTarget : null;
    const dialogComponent = document.getElementById('quick-add-dialog');
    const quickAddDialog = dialogComponent instanceof QuickAddDialog ? dialogComponent : null;
    const requestId = ++quickAddModalRequestId;
    const currentUrl = this.productPageUrl;
    if (this.dataset.usesSellingPlans === 'true') {
      if (currentUrl) window.location.href = currentUrl;
      return;
    }

    this.#setButtonLoadingState(triggerButton, true);

    if (quickAddDialog) {
      this.#stayVisibleUntilDialogCloses(quickAddDialog);
      quickAddDialog.setLoadingState(true);
      quickAddDialog.showDialog();
    }

    try {
      const productGrid = await this.#loadProductGrid(currentUrl);

      if (requestId !== quickAddModalRequestId) {
        return;
      }

      if (!this.isConnected) {
        quickAddDialog?.closeDialog();
        return;
      }

      if (!productGrid) {
        if (quickAddDialog) {
          quickAddDialog.closeDialog();
        }
        return;
      }

      // Use a fresh clone from the cache
      const freshContent = /** @type {Element} */ (productGrid.cloneNode(true));
      await this.updateQuickAddModal(freshContent);
      this.#updateVariantPicker(freshContent);

      if (!quickAddDialog) {
        this.#openQuickAddModal();
      }
    } catch (error) {
      if (requestId !== quickAddModalRequestId) return;

      if (isAbortError(error)) return;

      if (quickAddDialog) {
        quickAddDialog.closeDialog();
      }
      throw error;
    } finally {
      if (requestId === quickAddModalRequestId) {
        quickAddDialog?.setLoadingState(false);
        this.#setButtonLoadingState(triggerButton, false);
      }
    }
  };

  /**
   * Toggles a temporary loading state on quick add trigger buttons.
   * @param {HTMLButtonElement | null} button
   * @param {boolean} isLoading
   */
  #setButtonLoadingState(button, isLoading) {
    if (!button) return;

    if (isLoading) {
      button.dataset.wasDisabled = button.disabled ? 'true' : 'false';
      button.disabled = true;
      button.setAttribute('aria-busy', 'true');
      return;
    }

    button.disabled = button.dataset.wasDisabled === 'true';
    button.removeAttribute('aria-busy');
    delete button.dataset.wasDisabled;
  }

  #resetScroll() {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    const productDetails = dialogComponent.querySelector('.product-details');
    const productMedia = dialogComponent.querySelector('.product-information__media');
    productDetails?.scrollTo({ top: 0, behavior: 'instant' });
    productMedia?.scrollTo({ top: 0, behavior: 'instant' });
  }

  /** @param {QuickAddDialog} dialogComponent */
  #stayVisibleUntilDialogCloses(dialogComponent) {
    this.toggleAttribute('stay-visible', true);

    dialogComponent.addEventListener(DialogCloseEvent.eventName, () => this.toggleAttribute('stay-visible', false), {
      once: true,
    });
  }

  #openQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    this.#stayVisibleUntilDialogCloses(dialogComponent);

    dialogComponent.showDialog();

    // is nondeterministic when the open attribute is set on the dialog element after .showDialog() is called.
    // Waiting until the open animation starts seemed to be the most reliable metric here.
    const dialog = dialogComponent.refs?.dialog;
    if (!dialog) return;
    dialog.addEventListener('animationstart', this.#resetScroll.bind(this), { once: true });
  };

  #closeQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    dialogComponent.closeDialog();
  };

  /**
   * Fetches the product page content
   * @param {string} productPageUrl - The URL of the product page to fetch
   * @returns {Promise<Document | null>}
   */
  async fetchProductPage(productPageUrl) {
    if (!productPageUrl) return null;

    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    const abortController = new AbortController();
    this.#abortController = abortController;

    try {
      const response = await fetch(productPageUrl, {
        signal: abortController.signal,
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch product page: HTTP error ${response.status}`);
      }

      const responseText = await response.text();
      const html = new DOMParser().parseFromString(responseText, 'text/html');

      return html;
    } catch (error) {
      if (isAbortError(error)) {
        return null;
      }

      throw error;
    } finally {
      if (this.#abortController === abortController) {
        this.#abortController = null;
      }
    }
  }

  /**
   * Re-renders the variant picker.
   * @param {Element} productGrid - The product grid element
   */
  async updateQuickAddModal(productGrid) {
    const modalContent = document.getElementById('quick-add-modal-content');

    if (!productGrid || !modalContent) return;

    const productPageUrl = this.productPageUrl;
    if (lastModalProductUrl !== productPageUrl) {
      modalContent.replaceChildren();
    }

    if (isMobileBreakpoint()) {
      const productDetails = productGrid.querySelector('.product-details');
      const productFormComponent = productGrid.querySelector('product-form-component');
      const variantPicker = productGrid.querySelector('variant-picker');
      const productPrice = productGrid.querySelector('product-price');
      const productTitle = document.createElement('a');
      productTitle.textContent = this.dataset.productTitle || '';

      // Make product title as a link to the product page
      productTitle.href = this.productPageUrl;

      const productHeader = document.createElement('div');
      productHeader.classList.add('product-header');

      productHeader.appendChild(productTitle);
      if (productPrice) {
        productHeader.appendChild(productPrice);
      }
      productGrid.appendChild(productHeader);

      if (variantPicker) {
        productGrid.appendChild(variantPicker);
      }
      if (productFormComponent) {
        productGrid.appendChild(productFormComponent);
      }

      productDetails?.remove();
    }

    morph(modalContent, productGrid);

    this.#syncVariantSelection(modalContent);
    lastModalProductUrl = productPageUrl;
  }

  /**
   * Updates the quick-add button state based on whether a swatch is selected
   * @param {ProductSelectEvent} event - The product select event
   */
  #updateQuickAddButtonState(event) {
    if (!(event.target instanceof HTMLElement)) return;
    if (event.target.closest('product-card') !== this.closest('product-card')) return;
    // Selling-plan products must stay on Choose options (a plan can't be picked from a direct add).
    if (this.dataset.usesSellingPlans === 'true') return;
    const productOptionsCount = this.dataset.productOptionsCount;
    let quickAddButton = productOptionsCount === '1' ? 'add' : 'choose';

    // A single-option card can resolve to an unavailable variant (e.g. re-selecting a
    // sold-out swatch). Keep "Choose" so shoppers reach the picker, not a dead-end disabled "Add".
    if (quickAddButton === 'add' && this.#isSelectedVariantUnavailable()) {
      quickAddButton = 'choose';
    }

    this.setAttribute('data-quick-add-button', quickAddButton);
    if (quickAddButton === 'choose') {
      this.#schedulePrefetch();
      return;
    }
    this.#cancelScheduledPrefetch();
  }

  /**
   * Whether the card's currently selected swatch maps to an unavailable variant.
   * Reads `data-option-available` off the variant picker's selected option - the same
   * signal the product card uses. Only reports true on an explicit `false`, so an
   * unknown/absent signal leaves the caller's default ("add") untouched.
   * @returns {boolean}
   */
  #isSelectedVariantUnavailable() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    return productCard?.variantPicker?.selectedOption?.dataset.optionAvailable === 'false';
  }

  /**
   * Syncs the variant selection from the product card to the modal
   * @param {Element} modalContent - The modal content element
   */
  #syncVariantSelection(modalContent) {
    const selectedVariantId = this.#getSelectedVariantId();
    if (!selectedVariantId) return;

    // Find and check the corresponding input in the modal
    const modalInputs = modalContent.querySelectorAll('input[type="radio"][data-variant-id]');
    for (const input of modalInputs) {
      if (input instanceof HTMLInputElement && input.dataset.variantId === selectedVariantId && !input.checked) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
        break;
      }
    }
  }
}

if (!customElements.get('quick-add-component')) {
  customElements.define('quick-add-component', QuickAddComponent);
}

class QuickAddDialog extends DialogComponent {
  #abortController = new AbortController();

  /**
   * Toggles loading state visibility while quick-add content is fetched.
   * @param {boolean} isLoading
   */
  setLoadingState(isLoading) {
    const modalContent = document.getElementById('quick-add-modal-content');
    const loadingIndicator = this.querySelector('[data-quick-add-loading]');

    this.toggleAttribute('data-loading', isLoading);

    if (modalContent) {
      modalContent.setAttribute('aria-busy', String(isLoading));
    }

    if (loadingIndicator instanceof HTMLElement) {
      loadingIndicator.hidden = !isLoading;
    }
  }

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener(StandardEvents.cartLinesUpdate, this.handleCartUpdate, { signal: this.#abortController.signal });
    this.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal: this.#abortController.signal });

    this.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  /**
   * Closes the dialog after a successful cart lines update settles.
   * @param {import('@shopify/events').CartLinesUpdateEvent} event - The cart update event
   */
  handleCartUpdate = (event) => {
    event.promise
      ?.then(({ detail }) => {
        if (detail?.didError) return;
        this.closeDialog();
      })
      .catch(() => {
        // Keep the dialog open on error
      });
  };

  /** @param {ProductSelectEvent} event - The product select event */
  #handleProductSelect = (event) => {
    event.promise
      ?.then(({ detail }) => {
        if (!detail?.html) return;

        const { html } = detail;
        const anchorElement = /** @type {HTMLAnchorElement} */ (html.querySelector('.view-product-title a'));
        const viewMoreDetailsLink = /** @type {HTMLAnchorElement} */ (this.querySelector('.view-product-title a'));
        const mobileProductTitle = /** @type {HTMLAnchorElement} */ (this.querySelector('.product-header a'));

        if (!anchorElement) return;

        if (viewMoreDetailsLink) viewMoreDetailsLink.href = anchorElement.href;
        if (mobileProductTitle) mobileProductTitle.href = anchorElement.href;
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[quick-add] Event promise rejected:', error);
      });
  };

  #handleDialogClose = () => {
    const iosVersion = getIOSVersion();
    /**
     * This is a patch to solve an issue with the UI freezing when the dialog is closed.
     * To reproduce it, use iOS 16.0.
     */
    if (!iosVersion || iosVersion.major >= 17 || (iosVersion.major === 16 && iosVersion.minor >= 4)) return;

    requestAnimationFrame(() => {
      /** @type {HTMLElement | null} */
      const grid = document.querySelector('#ResultsList [product-grid-view]');
      if (grid) {
        const currentWidth = grid.getBoundingClientRect().width;
        grid.style.width = `${currentWidth - 1}px`;
        requestAnimationFrame(() => {
          grid.style.width = '';
        });
      }
    });
  };
}

if (!customElements.get('quick-add-dialog')) {
  customElements.define('quick-add-dialog', QuickAddDialog);
}
